/**
 * Campaign sidebar filter visibility (Settings → Campaigns).
 * Group defaults apply when no per-campaign override; overrides always win.
 */

export type CampaignOverride = 'show' | 'hide';

export type CampaignFilterVisibilityConfig = {
  groups?: Record<string, boolean>;
  campaigns?: Record<string, CampaignOverride>;
};

export function normCampaignGroupKey(name: string | null | undefined): string {
  const t = (name ?? '').trim();
  return t || '__ungrouped__';
}

/** Label for UI (group header). */
export function displayGroupName(internalKey: string): string {
  return internalKey === '__ungrouped__' ? '(No group)' : internalKey;
}

/** Effective visibility for a catalog campaign row. Missing config ⇒ all visible. */
export function isCampaignFilterVisible(
  campaignId: number,
  groupName: string | null | undefined,
  config: CampaignFilterVisibilityConfig | null | undefined
): boolean {
  if (!config || (!config.groups && !config.campaigns)) return true;
  const idKey = String(campaignId);
  const ov = config.campaigns?.[idKey];
  if (ov === 'show') return true;
  if (ov === 'hide') return false;
  const g = normCampaignGroupKey(groupName);
  const groupOn = config.groups?.[g];
  if (groupOn === false) return false;
  return true;
}

/**
 * Campaign titles for the Status left menu only — from the synced catalog row,
 * respecting Settings → Campaigns visibility. No UC code strings from agent payloads.
 */
export function visibleCampaignTitlesForStatusSidebar(
  catalog: Array<{ campaign_id: number; campaign_title: string | null; campaign_group_name: string | null }>,
  config: CampaignFilterVisibilityConfig | null | undefined
): string[] {
  const titles: string[] = [];
  for (const row of catalog) {
    const t = row.campaign_title?.trim();
    if (!t) continue;
    if (!isCampaignFilterVisible(row.campaign_id, row.campaign_group_name, config)) continue;
    titles.push(t);
  }
  return titles.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
